// src/features/MyGymTV/UK/AddD6/AddD6.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Layout from '../../../../layout/Layout';
import UKDatePicker from '../../UK/UKDatePicker/UKDatePicker';
import './add-d6.scss';
import axios from 'axios';
import moment from 'moment';

const AddD6 = () => {
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        imageFile: null,
        imagePreview: null,
        filename: '',
        startDate: new Date(), // Default to today
        endDate: null
    });

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const isImage = file.type.startsWith('image/');
            const isVideo = file.type === 'video/mp4';

            if (!isImage && !isVideo) {
                setErrors({ ...errors, image: 'Please upload a valid image (PNG, JPG) or video (MP4) file' });
                return;
            }

            if (isImage) {
                // Existing image validation
                const img = new Image();
                const objectUrl = URL.createObjectURL(file);

                img.onload = () => {
                    const width = img.width;
                    const height = img.height;

                    if (width === 1080 && height === 1920) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            setFormData({
                                ...formData,
                                imageFile: file,
                                imagePreview: reader.result,
                                filename: formData.filename || file.name
                            });
                            setErrors({ ...errors, image: null });
                        };
                        reader.readAsDataURL(file);
                    } else {
                        setFormData({
                            ...formData,
                            imageFile: null,
                            imagePreview: null
                        });
                        setErrors({
                            ...errors,
                            image: `Invalid image dimensions. D6 content must be exactly 1080x1920 pixels (portrait). Your image is ${width}x${height}.`
                        });
                    }

                    URL.revokeObjectURL(objectUrl);
                };

                img.onerror = () => {
                    setErrors({ ...errors, image: 'Failed to load image. Please try a different file.' });
                    URL.revokeObjectURL(objectUrl);
                };

                img.src = objectUrl;
            } else if (isVideo) {
                // Video validation
                const video = document.createElement('video');
                const objectUrl = URL.createObjectURL(file);

                video.onloadedmetadata = () => {
                    const width = video.videoWidth;
                    const height = video.videoHeight;

                    if (width === 1080 && height === 1920) {
                        // Create preview (first frame)
                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');

                        video.currentTime = 0;
                        video.onseeked = () => {
                            ctx.drawImage(video, 0, 0, width, height);
                            const previewUrl = canvas.toDataURL('image/jpeg');

                            setFormData({
                                ...formData,
                                imageFile: file,
                                imagePreview: previewUrl,
                                filename: formData.filename || file.name
                            });
                            setErrors({ ...errors, image: null });
                            URL.revokeObjectURL(objectUrl);
                        };
                    } else {
                        setFormData({
                            ...formData,
                            imageFile: null,
                            imagePreview: null
                        });
                        setErrors({
                            ...errors,
                            image: `Invalid video dimensions. D6 content must be exactly 1080x1920 pixels (portrait). Your video is ${width}x${height}.`
                        });
                        URL.revokeObjectURL(objectUrl);
                    }
                };

                video.onerror = () => {
                    setErrors({ ...errors, image: 'Failed to load video. Please try a different file.' });
                    URL.revokeObjectURL(objectUrl);
                };

                video.src = objectUrl;
            }
        }
    };

    const handleDeleteImage = () => {
        setFormData({
            ...formData,
            imageFile: null,
            imagePreview: null
        });
    };

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
        if (errors[field]) {
            setErrors({ ...errors, [field]: null });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.imageFile) {
            newErrors.image = 'Please upload an image';
        }
        if (!formData.filename.trim()) {
            newErrors.filename = 'Filename is required';
        }
        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
            newErrors.endDate = 'End date must be after start date';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePublishClick = () => {
        if (validateForm()) {
            setShowConfirmModal(true);
        }
    };

    const handleConfirmPublish = async () => {
        setIsSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();
            formDataToSend.append('image', formData.imageFile);
            formDataToSend.append('filename', formData.filename);
            formDataToSend.append('startDate', moment(formData.startDate).format('YYYY-MM-DD'));
            formDataToSend.append('endDate', formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : '');

            // Get network name from first selected club
            const networkName = selectedClubs[0]?.NetworkName || 'UK Network';
            formDataToSend.append('networkName', networkName);

            // send club IDs as JSON array
            const clubIds = selectedClubs.map(c => c.ClubId);
            formDataToSend.append('clubIds', JSON.stringify(clubIds));

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/AddD6Content`,
                formDataToSend,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setShowConfirmModal(false);
            resetForm();

            alert(`Content published successfully to ${response.data.SuccessfulClubs} venues!`);
            navigate('/uk/review-d6');

        } catch (error) {
            console.error('Error publishing content:', error);
            setErrors({ submit: error.response?.data || 'Failed to publish content. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            imageFile: null,
            imagePreview: null,
            filename: '',
            startDate: null,
            endDate: null
        });
        setErrors({});
    };

    if (selectedClubs.length === 0) {
        return (
            <Layout>
                <div className="add-d6-container mt-5">
                    <Alert variant="warning">
                        Please select one or more venues from the menu to add content.
                    </Alert>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="add-d6-container mt-5">
                <Card>
                    <Card.Header>
                        <h4>Add D6 Content</h4>
                        <small className="text-muted">
                            Adding content for {selectedClubs.length} selected venue(s)
                        </small>
                    </Card.Header>
                    <Card.Body>
                        {errors.submit && (
                            <Alert
                                dismissible
                                onClose={() => setErrors({ ...errors, submit: null })}
                                variant="danger"
                            >
                                {errors.submit}
                            </Alert>
                        )}

                        <Form>
                            <Form.Group className="mb-4">
                                <Form.Label>Upload Image or Video (1080x1920 portrait) *</Form.Label>
                                {!formData.imagePreview ? (
                                    <>
                                        <Form.Control
                                            accept="image/*,video/mp4"
                                            isInvalid={!!errors.image}
                                            onChange={handleImageUpload}
                                            type="file"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.image}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            D6 content must be exactly 1080x1920 pixels in portrait orientation. Accepts images (PNG, JPG) or MP4 videos.
                                        </Form.Text>
                                    </>
                                ) : (
                                    <div className="image-preview-container">
                                        <img
                                            alt="Preview"
                                            className="img-thumbnail mb-2"
                                            src={formData.imagePreview}
                                            style={{ maxWidth: '300px', maxHeight: '300px' }}
                                        />
                                        <div className="mb-2">
                                            <span className="text-success">✓ Valid D6 content (1080x1920)</span>
                                        </div>
                                        <div>
                                            <Button
onClick={handleDeleteImage}
size="sm"
variant="danger"
                                            >
                                                Delete Content
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Form.Group>

                            {/* Filename */}
                            <Form.Group className="mb-3">
                                <Form.Label>Filename *</Form.Label>
                                <Form.Control
                                    isInvalid={!!errors.filename}
                                    onChange={(e) => handleInputChange('filename', e.target.value)}
                                    placeholder="Enter filename"
                                    type="text"
                                    value={formData.filename}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.filename}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {/* Date Range */}
                            <div className="row">
                                <div className="col-md-6">
                                    <UKDatePicker
                                        error={errors.startDate}
                                        label="Start Date *"
                                        minDate={new Date()}
                                        onChange={(date) => handleInputChange('startDate', date)}
                                        selected={formData.startDate}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <UKDatePicker
                                        error={errors.endDate}
                                        label="End Date (Optional)"
                                        minDate={formData.startDate || new Date()}
                                        onChange={(date) => handleInputChange('endDate', date)}
                                        placeholder="∞"
                                        selected={formData.endDate}
                                        showClearButton
                                    />
                                    <Form.Text className="text-muted">
                                        Leave empty for no end date
                                    </Form.Text>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    disabled={isSubmitting}
                                    onClick={resetForm}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                                <Button
                                    disabled={isSubmitting || !formData.imageFile || !!errors.image}
                                    onClick={handlePublishClick}
                                    variant="primary"
                                >
                                    Publish
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>

                {/* Confirmation Modal */}
                <Modal
                    onHide={() => setShowConfirmModal(false)}
                    show={showConfirmModal}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Publication</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p><strong>Filename:</strong> {formData.filename}</p>
                        <p><strong>When:</strong> {formData.startDate && moment(formData.startDate).format('DD/MM/YYYY')} to {formData.endDate && moment(formData.endDate).format('DD/MM/YYYY')}</p>
                        <p><strong>Where:</strong></p>
                        <ul>
                            {selectedClubs.map(club => (
                                <li key={club.ClubId}>{club.ClubName}</li>
                            ))}
                        </ul>
                        <p>Are you sure you want to publish this content?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            disabled={isSubmitting}
                            onClick={() => setShowConfirmModal(false)}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={isSubmitting}
                            onClick={handleConfirmPublish}
                            variant="primary"
                        >
                            {isSubmitting ? 'Publishing...' : 'Confirm'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Layout>
    );
};

export default AddD6;